import { parseMessageText } from '@/shared-question-model';

import {
  resolvePickedFollowUpPrompt,
  toFollowUpSpec,
} from './shared-responder-v2-core-follow-up-prompt.helpers';
import { resolveSpokenAffordances } from './shared-responder-v2-core-handsfree.affordances';
import {
  HANDS_FREE_SCRIPT_SLOTS,
  type HandsFreeScript,
} from './shared-responder-v2-core-handsfree.types';
import { resolveOptionDisplayLabels } from './shared-responder-v2-core-option.helpers';
import {
  selectCurrentSpec,
  selectCurrentThread,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  THREAD_KINDS,
  TURN_ROLES,
} from './shared-responder-v2-core-thread.consts';
import type {
  QuestionSpec,
  ResponderTurn,
} from './shared-responder-v2-core-thread.types';

const SPOKEN_LIST_SEPARATOR = ', ';
const SPOKEN_SENTENCE_SEPARATOR = '. ';

function resolveSpokenQuestionText(spec: QuestionSpec): string {
  if (spec.kind !== THREAD_KINDS.Message) {
    return spec.text;
  }

  const { title, body } = parseMessageText(spec.text);

  if (title === '') {
    return body;
  }

  return body === '' ? title : `${title}${SPOKEN_SENTENCE_SEPARATOR}${body}`;
}

/**
 * The assistant reads the options out by their display text, never by their
 * values: a value is an identifier the responder never sees, and `imageChoice`'s
 * option is a picture. `resolveOptionDisplayLabels` is the one reader of that
 * split, so a spec whose values are already readable text still speaks.
 */
function resolveSpokenOptionList(spec: QuestionSpec): string {
  return resolveOptionDisplayLabels(spec).join(SPOKEN_LIST_SEPARATOR);
}

function resolveQuestionPromptScript(
  spec: QuestionSpec,
  announcesOptions: boolean
): HandsFreeScript {
  const cacheKey = `${spec.questionId}:${HANDS_FREE_SCRIPT_SLOTS.QuestionPrompt}`;
  const voiceText = spec.voiceText ?? null;

  // Randomized questions too: the backend's voiceText lists options in authoring
  // order, not the card's shuffle, but skipping it drops the "Choose one" / "or
  // other" cues (legacy parity) until the backend returns option-less voice text.
  if (voiceText !== null) {
    return {
      cacheKey,
      slot: HANDS_FREE_SCRIPT_SLOTS.QuestionPrompt,
      text: voiceText,
    };
  }

  const optionList = announcesOptions ? resolveSpokenOptionList(spec) : '';
  const promptText = resolveSpokenQuestionText(spec);

  return {
    cacheKey,
    slot: HANDS_FREE_SCRIPT_SLOTS.QuestionPrompt,
    text: optionList === '' ? promptText : `${promptText} ${optionList}`,
  };
}

function resolveSpokenTurnText(turn: ResponderTurn): string {
  const transcript = turn.transcript?.trim() ?? '';

  return transcript === '' ? turn.text.trim() : transcript;
}

/**
 * An assistant turn with no text has nothing to say and is dropped rather than
 * played as silence.
 *
 * A picked follow-up's backend voice text already reads its options out; only a
 * transcript equal to the display text (the backend's fallback when voicing
 * fails) still needs them appended.
 */
function resolveTurnScript(
  turn: ResponderTurn,
  spec: QuestionSpec
): HandsFreeScript | null {
  const spokenText = resolveSpokenTurnText(turn);

  if (spokenText === '') {
    return null;
  }

  const pickedPrompt = resolvePickedFollowUpPrompt(spec.kind, turn);
  const isVoiceText = spokenText !== turn.text.trim();
  const optionList =
    pickedPrompt === null || isVoiceText
      ? ''
      : resolveSpokenOptionList(toFollowUpSpec(pickedPrompt, spec));

  return {
    cacheKey: turn.id,
    slot: HANDS_FREE_SCRIPT_SLOTS.TurnText,
    text: optionList === '' ? spokenText : `${spokenText} ${optionList}`,
  };
}

function resolveScriptRuns(
  state: ResponderSessionState
): readonly (readonly HandsFreeScript[])[] {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null) {
    return [];
  }

  const { announcesOptions } = resolveSpokenAffordances(
    getThreadBehaviour(spec.kind)
  );

  let currentRun: HandsFreeScript[] = [
    resolveQuestionPromptScript(spec, announcesOptions),
  ];
  const runs: HandsFreeScript[][] = [currentRun];

  for (const turn of thread.turns) {
    if (turn.role === TURN_ROLES.Responder) {
      currentRun = [];
      runs.push(currentRun);
      continue;
    }

    const script = resolveTurnScript(turn, spec);

    if (script !== null) {
      currentRun.push(script);
    }
  }

  return runs;
}

/**
 * Everything the assistant says for the current question, in order: the prompt
 * first, then each assistant turn the backend has added.
 */
export function resolveHandsFreeScripts(
  state: ResponderSessionState
): readonly HandsFreeScript[] {
  return resolveScriptRuns(state).flat();
}

export function resolveHandsFreeReplayCursor(
  state: ResponderSessionState
): string | null {
  const runs = resolveScriptRuns(state);
  const lastRunIndex = runs.findLastIndex((run) => run.length > 0);

  if (lastRunIndex <= 0) {
    return null;
  }

  const precedingScripts = runs.slice(0, lastRunIndex).flat();

  return precedingScripts.at(-1)?.cacheKey ?? null;
}

export function resolveHandsFreeUpcomingScripts(
  state: ResponderSessionState,
  spokenScriptKey: string | null,
  depth: number
): readonly HandsFreeScript[] {
  const scripts = resolveHandsFreeScripts(state);

  const spokenIndex = scripts.findIndex(
    (script) => script.cacheKey === spokenScriptKey
  );

  return scripts.slice(spokenIndex + 1, spokenIndex + 1 + depth);
}

export function resolveHandsFreeScript(
  state: ResponderSessionState,
  spokenScriptKey: string | null
): HandsFreeScript | null {
  return resolveHandsFreeUpcomingScripts(state, spokenScriptKey, 1)[0] ?? null;
}
